import React, { useRef, useId, forwardRef, useEffect } from 'react';

/**
 * TextArea Component
 * 
 * Production-grade textarea with comprehensive features:
 * - Auto-resize (grows with content)
 * - Character and word counter
 * - Min/max height constraints
 * - Label with error and helper text
 * - Resize control (none, vertical, horizontal, both)
 * - Touch-friendly on mobile
 * - Fully accessible (ARIA labels, error announcements)
 * - Dark mode compatible
 * - Mobile-first responsive
 * 
 * @example
 * ```tsx
 * <TextArea
 *   label="Description"
 *   value={description}
 *   onChange={setDescription}
 *   placeholder="Enter description"
 *   autoResize
 *   maxLength={500}
 *   showCharCount
 *   required
 * />
 * ```
 */

export interface TextAreaProps {
  /** Textarea label text */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Textarea value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Error message (shows error state) */
  error?: string;
  /** Helper text (shown below textarea) */
  helperText?: string;
  /** Maximum character length */
  maxLength?: number;
  /** Show character counter */
  showCharCount?: boolean;
  /** Show word counter */
  showWordCount?: boolean;
  /** Initial number of rows */
  rows?: number;
  /** Maximum rows for auto-resize */
  maxRows?: number;
  /** Minimum height in pixels */
  minHeight?: number;
  /** Maximum height in pixels */
  maxHeight?: number;
  /** Enable auto-resize */
  autoResize?: boolean;
  /** Resize behavior */
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  /** Required field indicator */
  required?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Read-only state */
  readOnly?: boolean;
  /** Full width (100%) */
  fullWidth?: boolean;
  /** Auto-focus on mount */
  autoFocus?: boolean;
  /** Name attribute */
  name?: string;
  /** ID attribute (auto-generated if not provided) */
  id?: string;
  /** Blur event handler */
  onBlur?: () => void;
  /** Focus event handler */
  onFocus?: () => void;
  /** Key down event handler */
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /** Additional CSS classes */
  className?: string;
  /** ARIA label for accessibility */
  ariaLabel?: string;
  /** ARIA described by for accessibility */
  ariaDescribedBy?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      placeholder,
      value,
      onChange,
      error,
      helperText,
      maxLength,
      showCharCount = false,
      showWordCount = false,
      rows = 3,
      maxRows,
      minHeight,
      maxHeight,
      autoResize = false,
      resize = 'vertical',
      required = false,
      disabled = false,
      readOnly = false,
      fullWidth = false,
      autoFocus = false,
      name,
      id: providedId,
      onBlur,
      onFocus,
      onKeyDown,
      className = '',
      ariaLabel,
      ariaDescribedBy,
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const generatedId = useId();
    const id = providedId || generatedId;
    const errorId = `${id}-error`;
    const helperId = `${id}-helper`;
    const counterId = `${id}-counter`;

    // Calculate word count
    const wordCount = value.trim().length === 0 ? 0 : value.trim().split(/\s+/).length;

    // Auto-resize effect
    useEffect(() => {
      if (!autoResize || !textareaRef.current) return;

      const textarea = textareaRef.current;
      
      // Reset height to get correct scrollHeight
      textarea.style.height = 'auto';
      
      // Calculate new height
      let newHeight = textarea.scrollHeight;
      
      // Apply maxRows constraint
      if (maxRows) {
        const lineHeight = parseInt(getComputedStyle(textarea).lineHeight);
        const maxHeightFromRows = lineHeight * maxRows;
        newHeight = Math.min(newHeight, maxHeightFromRows);
      }
      
      // Apply maxHeight constraint
      if (maxHeight) {
        newHeight = Math.min(newHeight, maxHeight);
      }
      
      // Apply minHeight constraint
      if (minHeight) {
        newHeight = Math.max(newHeight, minHeight);
      }
      
      textarea.style.height = `${newHeight}px`;
    }, [value, autoResize, maxRows, minHeight, maxHeight]);

    // Merge refs
    useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(textareaRef.current);
        } else {
          ref.current = textareaRef.current;
        }
      }
    }, [ref]);

    // Resize classes
    const resizeClasses = {
      none: 'resize-none',
      vertical: 'resize-y',
      horizontal: 'resize-x',
      both: 'resize',
    };

    // Base textarea classes
    const textareaBaseClasses = `
      w-full
      rounded-lg
      border
      bg-white dark:bg-slate-800
      text-gray-900 dark:text-slate-100
      placeholder-gray-400 dark:placeholder-slate-500
      transition-all
      duration-200
      focus:outline-none
      focus:ring-2
      text-base
      px-4
      py-3
      min-h-[100px] sm:min-h-[80px]
      ${resizeClasses[resize]}
      ${autoResize ? 'overflow-hidden' : 'overflow-auto'}
      ${disabled ? 'cursor-not-allowed opacity-50' : ''}
      ${readOnly ? 'cursor-default bg-gray-50 dark:bg-slate-900' : ''}
      ${error
        ? 'border-red-500 dark:border-red-400 focus:ring-red-500/20 focus:border-red-600'
        : 'border-gray-300 dark:border-slate-600 focus:ring-blue-500/20 focus:border-blue-500 dark:focus:border-blue-400'
      }
    `.trim().replace(/\s+/g, ' ');

    // Container classes
    const containerClasses = `
      ${fullWidth ? 'w-full' : ''}
      ${className}
    `.trim();

    // Label classes
    const labelClasses = `
      block
      font-medium
      text-sm
      mb-1.5
      ${error ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-slate-300'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    `.trim().replace(/\s+/g, ' ');

    // Handle change
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      // Don't trigger onChange if disabled or readOnly (industry standard)
      if (disabled || readOnly) {
        return;
      }
      
      const newValue = e.target.value;

      // Enforce maxLength if specified
      if (maxLength && newValue.length > maxLength) {
        return;
      }

      onChange(newValue);
    };

    // Build aria-describedby
    const ariaDescriptions = [
      error ? errorId : null,
      helperText ? helperId : null,
      showCharCount || showWordCount ? counterId : null,
      ariaDescribedBy,
    ]
      .filter(Boolean)
      .join(' ');

    // Build style object for height constraints
    const textareaStyle: React.CSSProperties = {};
    if (minHeight && !autoResize) {
      textareaStyle.minHeight = `${minHeight}px`;
    }
    if (maxHeight && !autoResize) {
      textareaStyle.maxHeight = `${maxHeight}px`;
    }

    return (
      <div className={containerClasses}>
        {/* Label */}
        {label && (
          <label htmlFor={id} className={labelClasses}>
            {label}
            {required && <span className="text-red-500 ml-1" aria-label="required">*</span>}
          </label>
        )}

        {/* TextArea */}
        <textarea
          ref={textareaRef}
          id={id}
          name={name}
          value={value}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          rows={autoResize ? 1 : rows}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          autoFocus={autoFocus}
          maxLength={maxLength}
          className={textareaBaseClasses}
          style={textareaStyle}
          aria-label={ariaLabel || label}
          aria-describedby={ariaDescriptions || undefined}
          aria-invalid={error ? 'true' : 'false'}
          aria-required={required}
        />

        {/* Error Message / Helper Text / Counters Container */}
        {(error || helperText || showCharCount || showWordCount) && (
          <div className="flex items-start justify-between gap-2 mt-1.5">
            {/* Error or Helper Text */}
            <div className="flex-1 min-w-0">
              {error && (
                <p
                  id={errorId}
                  className="text-sm text-red-600 dark:text-red-400"
                  role="alert"
                  aria-live="polite"
                >
                  {error}
                </p>
              )}
              {!error && helperText && (
                <p
                  id={helperId}
                  className="text-sm text-gray-500 dark:text-slate-400"
                >
                  {helperText}
                </p>
              )}
            </div>

            {/* Character and Word Counters */}
            {(showCharCount || showWordCount) && (
              <div
                id={counterId}
                className="flex flex-col items-end gap-0.5 flex-shrink-0"
                aria-live="polite"
                aria-atomic="true"
              >
                {showCharCount && maxLength && (
                  <span
                    className={`
                      text-xs
                      ${value.length >= maxLength
                        ? 'text-red-600 dark:text-red-400 font-medium'
                        : 'text-gray-400 dark:text-slate-500'
                      }
                    `}
                  >
                    {value.length} / {maxLength} characters
                  </span>
                )}
                {showCharCount && !maxLength && (
                  <span className="text-xs text-gray-400 dark:text-slate-500">
                    {value.length} characters
                  </span>
                )}
                {showWordCount && (
                  <span className="text-xs text-gray-400 dark:text-slate-500">
                    {wordCount} {wordCount === 1 ? 'word' : 'words'}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';

